// Helpers for the schedule timeline's "filter by stage" UI, shared by the
// schedule creator (CrewSchedule) and the read-only producer view
// (ReadOnlyScheduleTimeline). Producers filter to one or more stages to
// see, e.g., how often the overview / audience is filmed at a given stage.
//
// Mirrors the shape of crewFunctionFilter.ts: pure functions so the
// inclusion/exclusion contract is unit-testable independent of the render.

import { toggleInSet } from "@/lib/toggleInSet";

export type StageLike = { label: string };

// Distinct, non-empty stage labels in first-seen (display) order — drives
// the filter button row. Stages have a meaningful display order, so unlike
// the function filter we do NOT sort alphabetically. Empty input → empty
// list (the filter UI then hides itself).
export function listStageLabels(stages: StageLike[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of stages) {
    const label = s.label?.trim();
    if (label && !seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  }
  return out;
}

// Convenience filter for whole stage lists. Preserves input order.
export function applyStageFilter<T extends StageLike>(
  stages: T[],
  selected: ReadonlySet<string>,
): T[] {
  if (selected.size === 0) return stages;
  return stages.filter((s) => selected.has(s.label));
}

// Toggle a label in/out of the selection. Thin alias over the shared
// toggleInSet so stage and function filters can't drift apart.
export function toggleStage(selected: ReadonlySet<string>, label: string): Set<string> {
  return toggleInSet(selected, label);
}
